import { v4 as uuid } from "uuid";

// Add alpha to hex color
export const hexWithOpacity = (hexColor: string, opacity: number) => {
  var _opacity = Math.round(Math.min(Math.max(opacity, 0), 1) * 255);
  return hexColor + _opacity.toString(16).toUpperCase();
};

// Set nested property of object
export const setNestedProperty = (obj: any, path: string, value: any): any => {
  const [head, ...rest] = path.split(".");

  return {
    ...obj,
    [head]: rest?.length
      ? setNestedProperty(obj[head], rest.join("."), value)
      : value,
  };
};

export const secondsToTime = (seconds: number) => {
  if (isNaN(seconds)) {
    return "00:00";
  }
  const date = new Date(0);
  date.setSeconds(seconds);
  return date.toISOString().slice(14, 19);
};

export const valueExists = (value: string | number | undefined) => {
  return value !== undefined && value !== null && value !== "";
};

type RGB = { r: number; g: number; b: number };
type HSL = { h: number; s: number; l: number };

function hexToRgb(hex: string): RGB {
  hex = hex.replace(/^#/, "");
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("");
  }
  const num = parseInt(hex, 16);
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
}

function rgbToHex({ r, g, b }: RGB): string {
  return (
    "#" +
    [r, g, b]
      .map((x) => {
        const hex = x.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")
  );
}

function rgbToHsl({ r, g, b }: RGB): HSL {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r:
        h = (g - b) / delta + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / delta + 2;
        break;
      case b:
        h = (r - g) / delta + 4;
        break;
    }
    h /= 6;
  }

  return { h, s, l };
}

function hslToRgb({ h, s, l }: HSL): RGB {
  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p: number, q: number, t: number): number => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
}

export function lightenHexColor(hex: string): string {
  const rgb = hexToRgb(hex);
  const hsl = rgbToHsl(rgb);
  hsl.l = 0.9; // Set lightness to 100%
  const lightRgb = hslToRgb(hsl);
  return rgbToHex(lightRgb);
}

export const getMediumLevelColor = (hexColor: string): string => {
  const rgb = hexToRgb(hexColor);
  const hsl = rgbToHsl(rgb);
  const lightness = hsl.l;
  hsl.l = Math.min(1, lightness - lightness * 0.1); // Decrease lightness by 10%
  const saturation = hsl.s;
  hsl.s = Math.max(0, saturation - saturation * 0.05); // Decrease saturation by 5%
  const hue = hsl.h;
  hsl.h = Math.max(0, hue + hue * 0.05); // Increase hue by 5%
  const mediumLevelRgb = hslToRgb(hsl);
  return rgbToHex(mediumLevelRgb);
};

export const getAdvancedLevelColor = (hexColor: string): string => {
  const rgb = hexToRgb(hexColor);
  const hsl = rgbToHsl(rgb);
  const lightness = hsl.l;
  hsl.l = Math.min(1, lightness - lightness * 0.2); // Decrease lightness by 20%
  const saturation = hsl.s;
  hsl.s = Math.max(0, saturation - saturation * 0.3); // Decrease saturation by 20%
  const hue = hsl.h;
  hsl.h = Math.min(1, hue + hue * 0.1); // Increase hue by 10%
  const advancedLevelRgb = hslToRgb(hsl);
  return rgbToHex(advancedLevelRgb);
};

export const getBrandingMediumColor = (primaryHex: string): string => {
  const rgb = hexToRgb(primaryHex);
  const hsl = rgbToHsl(rgb);
  hsl.l = 0.55; // Set lightness to 55% for medium branding color
  hsl.s = 0.3; // Set saturation to 30% for medium branding color
  const mediumRgb = hslToRgb(hsl);
  return rgbToHex(mediumRgb);
};

export const getBrandingDarkColor = (primaryHex: string): string => {
  const rgb = hexToRgb(primaryHex);
  const hsl = rgbToHsl(rgb);
  hsl.l = 0.2; // Set lightness to 20% for dark branding color
  hsl.s = 0.2; // Set saturation to 20% for dark branding color
  const darkRgb = hslToRgb(hsl);
  return rgbToHex(darkRgb);
};

export const getBrandingLightColor = (primaryHex: string): string => {
  const rgb = hexToRgb(primaryHex);
  const hsl = rgbToHsl(rgb);
  hsl.s = 0.2; // Set saturation to 20% for light branding color
  hsl.l = 0.92; // Set lightness to 92% for light branding color
  const lightRgb = hslToRgb(hsl);
  return rgbToHex(lightRgb);
};

export const getUuid = (): string => {
  return uuid();
};

export const getUserAge = (birthDateString: string): number => {
  const birthDate = new Date(birthDateString);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();

  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age;
};
